import { Form, FormField, RequiredValidator, IFormField, Validator } from '@thinknimble/tn-forms'
import i18next from '../../i18n/config'

const { t } = i18next
/**
 * Amounts are never negative: Credit or Debit (cash flow type) carries the direction.
 * Empty passes, so RequiredValidator owns the missing-value message.
 */
export class NonNegativeAmountValidator extends Validator {
  constructor({ message = t('expenseForm.amountNegative'), code = 'negativeAmount' } = {}) {
    super({ message, code, isRequired: false })
  }

  call(value: string | number | null | undefined) {
    if (value === null || value === undefined || value === '') return
    if (Number(String(value).replace(',', '.')) < 0) {
      throw new Error(JSON.stringify({ code: this.code, message: this.message }))
    }
  }
}

type SelectOption = {
  label: string
  value: string
}

// `value` carries the ExpenseCategory's char value (for matching AI-guessed
// strings against the options list); `categoryId` is the real FK id sent to
// the backend.
export type CategorySelectOption = SelectOption & {
  categoryId: string | null
}

export type ExpenseFormInputs = {
  _name: IFormField<string>
  amount: IFormField<string | number>
  amountFloat: IFormField<number>
  notes: IFormField<string>
  isReimbursable: IFormField<boolean>
  isReimbursed: IFormField<boolean>
  expenseDate: IFormField<string>
  organizationRef: IFormField<SelectOption | null>
  receipts: IFormField<string[]>
  reimburseToRef: IFormField<SelectOption | null>
  amountCurrency: IFormField<string>
  providedId: IFormField<string>
  categoryRef: IFormField<CategorySelectOption | null>
  cashFlowTypeRef: IFormField<SelectOption | null>
  reportsRef: IFormField<SelectOption[]>
  paymentAccountRef: IFormField<SelectOption | null>
  vendorRef: IFormField<SelectOption | null>
  // Reconciliation and Tax fields
  isReconciled: IFormField<boolean>
  taxYear: IFormField<string>
  transactionTypeRef: IFormField<SelectOption | null>
  personalUsePercentage: IFormField<string>
  milesDriven: IFormField<string>
  actualConvertedAmount: IFormField<string>
}

export class ExpenseForm extends Form<ExpenseFormInputs> {
  static _name = new FormField({
    label: t('expenseForm.nameLabel'),
    placeholder: t('expenseForm.namePlaceholder'),
    validators: [new RequiredValidator()],
    type: 'text',
  })
  static amount = new FormField({
    label: t('expenseForm.amountLabel'),
    placeholder: t('expenseForm.amountPlaceholder'),
    validators: [new RequiredValidator(), new NonNegativeAmountValidator()],
    value: '',
    type: 'text',
  })
  static amountFloat = new FormField({
    label: t('expenseForm.amountLabel'),
    placeholder: t('expenseForm.amountPlaceholder'),
    validators: [new RequiredValidator()],
    value: '',
    type: 'text',
  })

  static expenseDate = new FormField({
    label: t('expenseForm.expenseDateLabel'),
    placeholder: t('expenseForm.expenseDatePlaceholder'),
    validators: [new RequiredValidator()],
    value: '',
    type: 'date',
  })
  static isReimbursable = new FormField({
    label: t('expenseForm.isReimbursableLabel'),
    placeholder: t('expenseForm.isReimbursablePlaceholder'),
    validators: [new RequiredValidator()],
    value: false,
    type: 'checkbox',
  })
  static isReimbursed = new FormField({
    label: t('expenseForm.isReimbursedLabel'),
    placeholder: t('expenseForm.isReimbursedPlaceholder'),
    validators: [new RequiredValidator()],
    value: false,
    type: 'checkbox',
  })
  static amountCurrency = new FormField({
    label: t('expenseForm.amountCurrencyLabel'),
    placeholder: t('expenseForm.amountCurrencyPlaceholder'),
    validators: [new RequiredValidator()],
    type: 'option',
    value: 'COP',
  })

  static organizationRef = new FormField({
    label: t('expenseForm.organizationLabel'),
    placeholder: t('expenseForm.organizationPlaceholder'),
    validators: [
      new RequiredValidator({
        message: t('expenseForm.errors.required'),
      }),
    ],
    type: 'option',
  })

  static reimburseToRef = new FormField({
    label: t('expenseForm.reimburseToLabel'),
    placeholder: t('expenseForm.reimburseToPlaceholder'),
    validators: [],
    type: 'option',
  })

  static receipts = new FormField({
    label: t('expenseForm.receiptsLabel'),
    placeholder: t('expenseForm.receiptsPlaceholder'),
    validators: [],
    type: 'option',
    value: [],
  })

  static notes = new FormField({
    label: t('expenseForm.notesLabel'),
    placeholder: t('expenseForm.notesPlaceholder'),
    validators: [],
    type: 'long-text',
  })

  static providedId = new FormField({
    label: t('expenseForm.providedIdLabel'),
    placeholder: t('expenseForm.providedIdPlaceholder'),
    validators: [],
    type: 'text',
  })

  static cashFlowTypeRef = new FormField({
    label: t('expenseForm.cashFlowTypeLabel'),
    placeholder: t('expenseForm.cashFlowTypePlaceholder'),
    validators: [
      new RequiredValidator({
        message: t('expenseForm.errors.required'),
      }),
    ],
    type: 'option',
  })
  static categoryRef = new FormField({
    label: t('expenseForm.categoryLabel'),
    placeholder: t('expenseForm.categoryPlaceholder'),
    validators: [],
    type: 'option',
  })
  static reportsRef = new FormField({
    label: t('expenseForm.reportLabel'),
    placeholder: t('expenseForm.reportPlaceholder'),
    validators: [],
    type: 'option',
    value: [],
  })
  static paymentAccountRef = new FormField({
    label: t('expenseForm.paymentAccountLabel'),
    placeholder: t('expenseForm.paymentAccountPlaceholder'),
    validators: [],
    type: 'option',
  })
  static vendorRef = new FormField({
    label: t('expenseForm.vendorLabel', { defaultValue: 'Vendor' }),
    placeholder: t('expenseForm.vendorPlaceholder', { defaultValue: 'Select a vendor' }),
    validators: [],
    type: 'option',
  })

  static isReconciled = new FormField({
    label: t('expenseForm.isReconciledLabel', { defaultValue: 'Reconciled' }),
    placeholder: t('expenseForm.isReconciledPlaceholder', {
      defaultValue: 'Transaction has been reconciled',
    }),
    validators: [],
    type: 'checkbox',
    value: false,
  })

  static taxYear = new FormField({
    label: t('expenseForm.taxYearLabel', { defaultValue: 'Tax Year' }),
    placeholder: t('expenseForm.taxYearPlaceholder', { defaultValue: 'YYYY' }),
    validators: [],
    type: 'text',
    value: '',
  })

  static transactionTypeRef = new FormField({
    label: t('expenseForm.transactionTypeLabel', { defaultValue: 'Transaction Type' }),
    placeholder: t('expenseForm.transactionTypePlaceholder', {
      defaultValue: 'Select transaction type',
    }),
    validators: [],
    type: 'option',
  })

  static personalUsePercentage = new FormField({
    label: t('expenseForm.personalUsePercentageLabel', {
      defaultValue: 'Personal Use Percentage',
    }),
    placeholder: t('expenseForm.personalUsePercentagePlaceholder', { defaultValue: '0.00' }),
    validators: [],
    type: 'text',
    value: '',
  })

  static milesDriven = new FormField({
    label: t('expenseForm.milesDrivenLabel', { defaultValue: 'Miles Driven' }),
    placeholder: t('expenseForm.milesDrivenPlaceholder', { defaultValue: '0.0' }),
    validators: [],
    type: 'text',
    value: '',
  })

  static actualConvertedAmount = new FormField({
    label: t('expenseForm.actualConvertedAmountLabel', { defaultValue: 'Actual amount' }),
    placeholder: t('expenseForm.actualConvertedAmountPlaceholder', { defaultValue: '0.00' }),
    validators: [],
    type: 'text',
    value: '',
  })

  get val() {
    // Hack because of _name field
    const amountVal = this.field.amountFloat.value
    // Parse numeric fields, handling empty strings
    const taxYearVal =
      this.field.taxYear.value && this.field.taxYear.value.trim()
        ? parseInt(this.field.taxYear.value, 10)
        : null
    const personalUsePercentageVal =
      this.field.personalUsePercentage.value && this.field.personalUsePercentage.value.trim()
        ? parseFloat(this.field.personalUsePercentage.value)
        : null
    const milesDrivenVal =
      this.field.milesDriven.value && this.field.milesDriven.value.trim()
        ? parseFloat(this.field.milesDriven.value)
        : null

    return {
      name: this.field._name.value,
      amount: amountVal,
      amountCurrency: this.field.amountCurrency.value,
      notes: this.field.notes.value,
      reimbursementNotes: '',
      isReimbursable: this.field.isReimbursable.value,
      isReimbursed: this.field.isReimbursed.value,
      expenseDate: this.field.expenseDate.value,
      organization: this.field.organizationRef.value?.value || null,
      receipts: this.field.receipts.value,
      reimburseTo: this.field.reimburseToRef.value?.value || null,
      providedId: this.field.providedId.value,
      expenseCategory: this.field.categoryRef.value?.categoryId || null,
      cashFlowType: this.field.cashFlowTypeRef.value?.value || null,
      reports: this.field.reportsRef.value?.map((r) => r.value) || [],
      paymentAccount: this.field.paymentAccountRef.value?.value || null,
      vendor: this.field.vendorRef.value?.value || null,
      // Reconciliation and Tax fields
      isReconciled: this.field.isReconciled.value || false,
      taxYear: taxYearVal,
      transactionType: this.field.transactionTypeRef.value?.value || null,
      personalUsePercentage: personalUsePercentageVal,
      milesDriven: milesDrivenVal,
      // Omitted entirely (rather than sent as null) when untouched, so the manual/auto
      // state on the backend is left alone unless the user actually typed a value.
      ...(this.field.actualConvertedAmount.value &&
      this.field.actualConvertedAmount.value.trim() !== ''
        ? { defaultConvertedAmount: parseFloat(this.field.actualConvertedAmount.value) }
        : {}),
    }
  }
}

export type TExpenseForm = ExpenseForm & ExpenseFormInputs

export type ExpenseFilterFormInputs = {
  search: IFormField<string>
  organization: IFormField<string>
  category: IFormField<string>
  cashFlowType: IFormField<string>
  expenseDate: IFormField<string>
  created: IFormField<string>
  report: IFormField<string>
  isReimbursable: IFormField<boolean>
  isReimbursed: IFormField<boolean>
}

export class ExpenseFilterForm extends Form<ExpenseFilterFormInputs> {
  static search = new FormField({
    label: t('expenseForm.searchLabel'),
    placeholder: t('expenseForm.searchPlaceholder'),
    validators: [],
    type: 'text',
  })
  static organization = new FormField({
    label: t('expenseForm.organizationLabel'),
    placeholder: t('expenseForm.organizationPlaceholder'),
    validators: [],
    type: 'option',
  })
  static category = new FormField({
    label: t('expenseForm.categoryLabel'),
    placeholder: t('expenseForm.categoryPlaceholder'),
    validators: [],
    type: 'option',
  })
  static cashFlowType = new FormField({
    label: t('expenseForm.cashFlowTypeLabel'),
    placeholder: t('expenseForm.cashFlowTypePlaceholder'),
    validators: [],
    type: 'option',
  })
  static expenseDate = new FormField({
    label: t('expenseForm.expenseDateLabel'),
    placeholder: t('expenseForm.expenseDatePlaceholder'),
    validators: [],
    type: 'date',
  })
  static created = new FormField({
    label: t('expenseForm.createdLabel'),
    placeholder: t('expenseForm.createdPlaceholder'),
    validators: [],
    type: 'date',
  })
  static report = new FormField({
    label: t('expenseForm.reportLabel'),
    placeholder: t('expenseForm.reportPlaceholder'),
    validators: [],
    type: 'option',
  })
  static isReimbursable = new FormField({
    label: t('expenseForm.isReimbursableLabel'),
    placeholder: t('expenseForm.isReimbursablePlaceholder'),
    validators: [],
    type: 'toggle',
    value: false,
  })
  static isReimbursed = new FormField({
    label: t('expenseForm.isReimbursedLabel'),
    placeholder: t('expenseForm.isReimbursedPlaceholder'),
    validators: [],
    type: 'toggle',
    value: false,
  })
}

export type TExpenseFilterForm = ExpenseFilterForm & ExpenseFilterFormInputs
